import { useDisclosure } from '@chakra-ui/react';
import {
  INewQuoteTypeReqSchema,
  INoteResSchema,
  IQuoteBasicRespSchema,
  IQuoteItem,
  IQuoteOfferingRespSchema,
  IQuoteRespSchema,
  NewQuoteTypeEnum,
  NoteMilestone,
  QuoteCancelationReasonEnum,
  QuoteItemAmendmentStatusEnum,
  QuoteStatusEnum,
  QuoteTypeEnum,
} from '@monetize/types/app';
import { ExperienceConfigurationKeys } from '@monetize/types/experience-config';
import { FC, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { handleApiErrorToast } from '../../api/axios';
import {
  doArchiveQuoteService,
  doCancelQuoteService,
  doCloneQuote,
  doRetractQuoteService,
} from '../../api/cpqService';
import { enqueueForQuote } from '../../api/quoteWriteChain';
import { useRevRecSettings } from '../../api/revenueService';
import { useDealRoomConfig } from '../../api/settingsService';
import { ROLES, ROUTES } from '../../constants';
import {
  QUOTE_ARCHIVE_ACTION_STATUSES,
  QUOTE_COPYABLE_STATUSES,
  QUOTE_ESIGN_CONTACT_RETRACTABLE_STATUSES,
} from '../../constants/quotes';
import { getDealRoomByDealRoom } from '../../constants/routes';
import {
  useExperienceConfiguration,
  useQuote,
  useQuoteSettings,
} from '../../hooks';
import { useIsTenantEsignConfigured } from '../../hooks/useIsTenantEsignConfigured';
import { useQuoteResendEsign } from '../../hooks/useQuoteResendEsign';
import { BlueprintModal } from '../../routes/Quotes/Quote/BlueprintModal';
import { ManualQuoteAcceptanceModal } from '../../routes/Quotes/Quote/ManualQuoteAcceptanceModal';
import ManualRenewalModal from '../../routes/Quotes/Quote/ManualRenewalModal';
import { useQuoteSendModalActions } from '../../routes/Quotes/QuoteV2/components/QuoteButtons/quoteSendButton/useQuoteSendModalActions';
import { useACL } from '../../services/acl/acl';
import { useAuth } from '../../services/auth0';
import { useConfirmModal } from '../../services/confirmModal';
import { useFlags } from '../../services/launchDarkly';
import { logger } from '../../services/logger';
import { useToast } from '../../services/toast';
import { canUserAcceptQuote } from '../../utils';
import { canCancelQuote } from '../../utils/quotes';
import { MBox, MFlex, MText } from '../Monetize';
import DataTableActions, {
  ActionType,
} from '../Monetize/DataTable/MDataTableActions';
import { QuoteDownloadMenu } from './QuoteDownloadMenu';
import { NotesDrawerV2 } from '../Notes/NotesDrawerV2';
import { NotesDrawerV2Menu, NotesTooltip } from '../Notes/NotesDrawerV2Menu';
import { QuoteAnalyticsV2 } from './quoteAnalytics/QuoteAnalyticsV2';

const getNotesMilestones = (
  quote: IQuoteBasicRespSchema & Partial<IQuoteRespSchema>,
): NoteMilestone[] => {
  const output: NoteMilestone[] = [];

  if (quote.approvalSubmittedAt) {
    output.push({
      date: quote.approvalSubmittedAt,
      label: 'Approval Submitted',
    });
  }

  if (quote.declinedDate) {
    output.push({
      date: quote.declinedDate,
      label: 'Approval Declined',
    });
  }

  if (quote.approvalGrantedAt) {
    output.push({
      date: quote.approvalGrantedAt,
      label: 'Approval Granted',
    });
  }

  if (quote.acceptedAt) {
    output.push({ date: quote.acceptedAt, label: 'Accepted' });
  }

  if (quote.processedAt) {
    output.push({ date: quote.processedAt, label: 'Processed' });
  }
  return output;
};

type QuoteActionProps = {
  quote: IQuoteBasicRespSchema & Partial<IQuoteRespSchema>;
  isQuotesList?: boolean;
  manualRenewalFormMethods?: UseFormReturn<INewQuoteTypeReqSchema, object>;
  loadData: () => void;
  setQuote: (quote: IQuoteRespSchema | null) => void;
  resetQuoteForm: (quote: IQuoteRespSchema) => void;
  onSendQuote: () => void;
};

export const QuoteActionsV2: FC<QuoteActionProps> = ({
  quote,
  isQuotesList,
  manualRenewalFormMethods,
  loadData,
  setQuote,
  resetQuoteForm,
  onSendQuote,
}: QuoteActionProps) => {
  const experienceConfigSettings = useExperienceConfiguration(
    ExperienceConfigurationKeys.quoteAndContractSettings,
  );
  const { enableRevRecV2 } = useFlags();
  const { quoteAcceptanceAllowedRoles, quoteManualAcceptanceAllowedRoles } =
    experienceConfigSettings;
  const { quoteSettings } = useQuoteSettings();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const isEsignEnabled = useIsTenantEsignConfigured();
  const { transitionQuoteStatus } = useQuote();
  const { showConfirmModal, setModalLoading, closeModal } = useConfirmModal();

  const { onClickSend: handleOpenSendQuoteModal } = useQuoteSendModalActions({
    isEsignEnabled,
    quote: quote as IQuoteRespSchema,
    onSendQuote,
  });

  const { data: recRevSettings } = useRevRecSettings();

  const {
    onOpen: onOpenManualAcceptanceModal,
    onClose: onCloseManualAcceptanceModal,
    open: isOpenManualAcceptanceModal,
  } = useDisclosure();
  const {
    onOpen: onOpenManualRenewalModal,
    onClose: onCloseManualRenewalModal,
    open: isOpenManualRenewalModal,
  } = useDisclosure();
  const {
    open: isNotesDrawerOpen,
    onOpen: onNotesDrawerOpen,
    onClose: onNotesDrawerClose,
  } = useDisclosure();
  const {
    onOpen: onOpenBlueprintModal,
    onClose: onCloseBlueprintModal,
    open: isOpenBlueprintModal,
  } = useDisclosure();

  const {
    open: isAnalyticsOpen,
    onOpen: onAnalyticsOpen,
    onClose: onAnalyticsClose,
  } = useDisclosure();

  const [analyticsTabIndex, setAnalyticsTabIndex] = useState(0);

  const { data: dealRoomConfig } = useDealRoomConfig();
  const { canDo } = useACL();
  const canUpdateSales = canDo([['sales', 'update']]);
  const canReadSales = canDo([['sales', 'read']]);
  const canUpdateBlueprintQuotes = canDo([['blueprint_quotes', 'update']]);
  const { currentTenantUser, currentTenantUserHasRole } = useAuth();
  const [unsavedNotes, setUnsavedNotes] = useState<Map<string, INoteResSchema>>(
    new Map(),
  );
  const [numberOfNotes, setNumberOfNotes] = useState(0);
  const { onResendQuote } = useQuoteResendEsign(quote.id);

  const canAcceptQuote =
    !!quoteSettings &&
    canUserAcceptQuote({
      pauseAtAccepted: !!quoteSettings?.pauseAtAccepted,
      quoteAcceptanceAllowedRoles,
      quoteManualAcceptanceAllowedRoles,
      currentTenantUserHasRole,
    });

  if (!quote) {
    return null;
  }

  const isAmendment = quote.type === QuoteTypeEnum.AMENDMENT;
  const { requiresEsign } = quote;

  // Check if the quote Item amendStatus is NO_CHANGE
  const ifAmendStatusIsNoChange = quote?.quoteOfferings
    ?.reduce(
      (acc: IQuoteItem[], curr: IQuoteOfferingRespSchema) =>
        curr.items ? [...acc, ...curr.items] : acc,
      [] as IQuoteItem[],
    )
    .every(
      (x: IQuoteItem) =>
        x.amendmentStatus === QuoteItemAmendmentStatusEnum.NO_CHANGE,
    );

  const handleCancelQuote = (
    data: IQuoteBasicRespSchema & Partial<IQuoteRespSchema>,
  ) => {
    const onYes = async () => {
      setModalLoading(true);
      try {
        data?.id &&
          (await enqueueForQuote(data.id, () =>
            doCancelQuoteService(data, QuoteCancelationReasonEnum.BY_USER),
          ));
      } catch (err) {
        logger.error(err);
        handleApiErrorToast(err);
      }
      setModalLoading(false);
      loadData();
      closeModal();
    };

    showConfirmModal({
      title: `Are you sure you want to cancel "${data.description}"?`,
      description:
        'A Quote cancelation cannot be undone, you can always create a new quote if needed.',
      onYes,
      yesBtnProps: {
        variant: 'delete' as any,
      },
      noBtnProps: {
        variant: 'cancel' as any,
      },
      yesButton: 'Confirm',
      noButton: 'Close',
    });
  };

  const handleArchiveQuote = (
    data: IQuoteBasicRespSchema & Partial<IQuoteRespSchema>,
  ) => {
    const onYes = async () => {
      try {
        setModalLoading(true);
        data?.id &&
          (await enqueueForQuote(data.id, () =>
            doArchiveQuoteService(data.id),
          ));
      } catch (error) {
        handleApiErrorToast(error);
      }
      setModalLoading(false);
      loadData();
      closeModal();
    };

    showConfirmModal({
      title: `Are you sure you want to archive Quote for "${data.description}"?`,
      description:
        'This cannot be undone and this Quote will not be returned in a list of results going forward.',
      onYes,
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
      yesButton: 'Archive',
      noButton: 'Cancel',
    });
  };

  const handleAcceptQuote = () => {
    onOpenManualAcceptanceModal();
  };

  const handleManualRenewal = () => {
    onOpenManualRenewalModal();
  };

  const handleProvisionQuote = async () => {
    const onYes = async () => {
      setModalLoading(true);
      try {
        await transitionQuoteStatus(quote.id, {
          newState: QuoteStatusEnum.ACCEPTED_ADMIN_EDITABLE,
        });
      } catch (err) {
        // pass
      }
      setModalLoading(false);
      loadData();
      closeModal();
    };

    showConfirmModal({
      title: 'Are you sure you want to edit this quote?',
      description:
        'Proceed with Caution: You are updating this information at your own risk. Please double-check all changes to ensure accuracy before submission.',
      onYes,
      noBtnProps: {
        variant: 'cancel',
      },
      yesButton: 'Edit',
      noButton: 'Close',
    });
  };

  const handleResendQuote = async () => {
    await onResendQuote();
  };

  const handleRetractQuote = async () => {
    const onYes = async () => {
      setModalLoading(true);
      try {
        quote.id &&
          (await enqueueForQuote(quote.id, () =>
            doRetractQuoteService(quote.id),
          ));
      } catch (err) {
        // pass
      }
      setModalLoading(false);
      loadData();
      closeModal();
    };

    showConfirmModal({
      title: 'Are you sure you want to retract this quote?',
      description:
        'Retracting a Quote will invalidate the eSign Document, you can always resend the quote for signature',
      onYes,
      yesBtnProps: {
        variant: 'delete',
      },
      noBtnProps: {
        variant: 'cancel',
      },
      yesButton: 'Retract',
      noButton: 'Close',
    });
  };

  const handleCloneQuote = async (quoteId: string) => {
    if (quoteId) {
      try {
        const quoteResponse = await doCloneQuote(quoteId);

        if (quoteResponse?.id) {
          navigate(ROUTES.getQuoteEditRoute(quoteResponse.id));
          resetQuoteForm(quoteResponse);
          addToast({
            summary: 'New Quote Created',
            detail: `${quoteResponse.description} has successfully been created.`,
            severity: 'success',
          });
        }
      } catch (err) {
        handleApiErrorToast(err);
      }
    }
  };

  const hasValidRole = currentTenantUserHasRole(ROLES.QUOTES_ROLES);

  const canShowRevenueAction =
    quote.status === QuoteStatusEnum.PROCESSED &&
    canUpdateSales &&
    enableRevRecV2 &&
    recRevSettings?.enabled &&
    !isQuotesList;

  if (!hasValidRole) {
    return null;
  }

  const canCancel = canCancelQuote({
    status: quote.status,
    canUpdateSales,
    isQuotesList,
  });

  const actions: ActionType[] = [
    {
      title: 'Manually Send',
      enabled:
        quote.status === QuoteStatusEnum.APPROVED &&
        !!quote.opportunity?.dealRoomId,
      action: () => handleOpenSendQuoteModal(),
    },
    {
      title: 'Open Deal Room',
      enabled: !!quote.opportunity?.dealRoomId && dealRoomConfig?.enabled,
      action: () =>
        quote.opportunity?.dealRoomId &&
        navigate(getDealRoomByDealRoom(quote.opportunity.dealRoomId)),
    },
    {
      title: 'Copy Quote',
      enabled: QUOTE_COPYABLE_STATUSES.has(quote.status) && canUpdateSales,
      action: () => handleCloneQuote(quote.id),
    },
    {
      actionElement: (
        <QuoteDownloadMenu key="Download" quote={quote} variant="menuItem" />
      ),
      enabled: canReadSales,
    },
    {
      title: quote?.metadata?.bluePrintId
        ? 'Edit Blueprint'
        : 'Use as Blueprint',
      enabled: !isQuotesList && canUpdateBlueprintQuotes,
      action: () => onOpenBlueprintModal(),
    },
    {
      actionElement: (
        <NotesDrawerV2Menu
          isOpen={isNotesDrawerOpen}
          entity="quotes"
          key={'Notes'}
          hasUnSavedNotes={unsavedNotes.size > 0}
          numberOfNotes={numberOfNotes}
          onOpen={onNotesDrawerOpen}
        />
      ),
      enabled: !isQuotesList && !!quote.id,
    },
    {
      title: 'Resend E-sign Email',
      enabled:
        !isQuotesList &&
        !!quote.esignEnvelopeId &&
        quote.status === QuoteStatusEnum.SENT &&
        canUpdateSales,
      action: () => handleResendQuote(),
    },
    {
      title: 'Retract E-sign',
      enabled:
        !isQuotesList &&
        !!quote.esignEnvelopeId &&
        QUOTE_ESIGN_CONTACT_RETRACTABLE_STATUSES.has(quote.status) &&
        canUpdateSales,
      action: () => handleRetractQuote(),
    },
    {
      title: 'Manually Accept',
      enabled:
        !isQuotesList &&
        quote.status === QuoteStatusEnum.SENT &&
        !(isAmendment && ifAmendStatusIsNoChange) &&
        !requiresEsign &&
        canAcceptQuote &&
        canUpdateSales,
      action: () => handleAcceptQuote(),
    },
    {
      title: 'Mark as Renewal',
      enabled:
        !isQuotesList &&
        quote.type === QuoteTypeEnum.NEW &&
        quote.status === QuoteStatusEnum.DRAFT &&
        quote.newQuoteType !== NewQuoteTypeEnum.MANUAL_RENEWAL &&
        canUpdateSales,
      action: () => handleManualRenewal(),
    },
    {
      title: 'Edit Quote (Admin Only)',
      enabled:
        !isQuotesList &&
        quote.status === QuoteStatusEnum.ACCEPTED &&
        canUpdateSales,
      action: () => handleProvisionQuote(),
    },
    {
      title: 'Revenue',
      enabled: canShowRevenueAction,
      action: () => {
        setAnalyticsTabIndex(3);
        onAnalyticsOpen();
      },
    },
    // Danger items should always remain at the bottom
    {
      title: 'Cancel',
      color: 'danger',
      enabled: canCancel,
      hasTopDivider: true,
      action: () => handleCancelQuote(quote),
    },
    {
      title: 'Archive',
      enabled:
        QUOTE_ARCHIVE_ACTION_STATUSES.has(quote.status) &&
        canUpdateSales &&
        quote.ownerId === currentTenantUser.id,
      hasTopDivider: !canCancel,
      action: () => handleArchiveQuote(quote),
    },
  ];

  return (
    <>
      <MBox>
        <MBox position="relative" display="flex" alignItems="flex-end">
          <DataTableActions
            actions={actions}
            iconContainerProps={{ minH: '8', minW: '8', btnSize: 6 }}
          />

          {!!numberOfNotes && (
            <MFlex
              zIndex={100}
              position="absolute"
              justifyContent="center"
              alignItems="center"
              top={-2}
              right={-2}
              width={5}
              height={5}
              borderRadius={10}
              bgColor={isQuotesList ? 'tGray.light' : 'tIndigo.base'}
              border="1px solid white"
            >
              <NotesTooltip numberOfNotes={numberOfNotes}>
                <MText
                  fontWeight="bold"
                  color={isQuotesList ? 'tBlack.base' : 'tWhite.base'}
                  mb={-0.5}
                  fontSize="xs"
                >
                  {numberOfNotes}
                </MText>
              </NotesTooltip>
            </MFlex>
          )}
        </MBox>
      </MBox>

      <ManualQuoteAcceptanceModal
        isOpen={isOpenManualAcceptanceModal}
        quoteId={quote.id}
        onClose={() => {
          onCloseManualAcceptanceModal();
          loadData();
        }}
      />

      {manualRenewalFormMethods && (
        <ManualRenewalModal
          isOpen={isOpenManualRenewalModal}
          quoteId={quote.id}
          manualRenewalForm={manualRenewalFormMethods}
          setQuote={setQuote}
          onClose={onCloseManualRenewalModal}
        />
      )}

      <BlueprintModal
        isOpen={isOpenBlueprintModal}
        quoteId={quote.id}
        blueprintId={quote?.metadata?.bluePrintId}
        onClose={onCloseBlueprintModal}
        onSuccess={loadData}
      />

      {/* Skip note check in list view */}
      {!isQuotesList && (
        <NotesDrawerV2
          id={quote.id}
          entity="quotes"
          milestones={getNotesMilestones(quote)}
          key={'Notes'}
          isOpen={isNotesDrawerOpen}
          unsavedNotes={unsavedNotes}
          setUnsavedNotes={setUnsavedNotes}
          setNotesWithoutMilestones={(notes) =>
            setNumberOfNotes(notes?.content?.length || 0)
          }
          onClose={onNotesDrawerClose}
        />
      )}

      {isAnalyticsOpen && (
        <QuoteAnalyticsV2
          quote={quote as IQuoteRespSchema}
          setQuote={setQuote}
          isOpen={isAnalyticsOpen}
          onClose={onAnalyticsClose}
          onOpen={onAnalyticsOpen}
          tabIndex={analyticsTabIndex}
          renderTrigger={() => null}
        />
      )}
    </>
  );
};
